import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import PaginationControls from '../../utils/PaginationControls';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { PICO_COLORS } from '../../utils/Picocolors';

// Hash category string to a PICO_COLORS value — for tag filter pills
const getPillColor = (category) => {
  const colors = Object.values(PICO_COLORS || {});
  if (!colors.length) return '#3b4eff';
  if (!category) return colors[0];
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Hash category string to a CSS badge class — for active tag pills above card grid
const getCategoryColorClass = (category) => {
  if (!category) return 'category-badge-1';
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `category-badge-${(Math.abs(hash) % 7) + 1}`;
};

// ─── Tag Filter Dropdown ──────────────────────────────────────────────────────
const CategoryMultiSelect = ({ 
  categories, 
  selectedCategories, 
  onChange 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tempSelected, setTempSelected] = useState(selectedCategories);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => 
      document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { 
    setTempSelected(selectedCategories); 
  }, [selectedCategories]);

  const validCategories = categories.filter(c => c && typeof c === 'string');
  const filtered = validCategories.filter(c => c.toLowerCase().includes(tagSearch.toLowerCase()));

  const handleToggle = (cat) => setTempSelected(prev =>
    prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
  );

  // Clear all selections
  const handleClearAll = () => { 
    setTempSelected([]); 
    onChange([]); 
    setIsOpen(false); 

  };
  const handleApply = () => { 
    onChange(tempSelected); 
    setIsOpen(false); 
  };

  return (
    <div className="ms-2 d-inline-block" ref={dropdownRef}>
      <div className="position-relative d-inline-block">
        <button
          type="button"
          className="ev-message-card2 ev-btn-xsmall ev-fs-11 ms-2"
          onClick={() => setIsOpen(o => !o)}
        >
          <i className="bi bi-tags me-1"></i>Tags
          {selectedCategories.length > 0 && ` (${selectedCategories.length})`}
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-1`}></i>
        </button>

        {isOpen && (
          <div className="tag-multiselect-dropdown position-absolute">
            {/* Search */}
            <div className="position-relative mb-2">
              <input
                type="text"
                className="form-control search-input ms-0"
                placeholder="Search tags..."
                value={tagSearch}
                onChange={e => setTagSearch(e.target.value)}
                autoFocus
              />
              <button type="button" className="ev-btn-lightblue-fill-round search-bar-button">
                <i className="bi bi-search"></i>
              </button>
            </div>

            {/* Select All / Deselect All */}
            <div className="d-flex justify-content-end mb-2">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall"
                onClick={() => setTempSelected([...validCategories])}>Select All</button>
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall ms-2"
                onClick={() => setTempSelected([])}>Deselect All</button>
            </div>

            {/* Pills — same style as SLR TagFilterPanel */}
            <div className="d-flex flex-wrap gap-1 align-items-start align-content-start scrollable-element vh-15 pt-1">
              {filtered.length === 0 ? (
                <span className="ev-xsmall-p-dark">No tags found</span>
              ) : filtered.map(cat => {
                const color = getPillColor(cat);
                const isSel = tempSelected.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    className="ev-btn-xsmall ev-bold-reg py-1 px-2 ev-radius-large"
                    onClick={() => handleToggle(cat)}
                    style={{
                      background: `${color}${isSel ? '33' : '15'}`,
                      color,
                      border: isSel ? `1.5px solid ${color}` : '1.5px solid transparent',
                    }}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-end gap-2 align-items-center mt-2 pt-2">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small"
                onClick={handleClearAll}>Clear All</button>
              <button type="button" className="ev-btn-yellow-fill-round ev-bold-reg"
                onClick={handleApply}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Tags display on fabric card ─────────────────────────────────────────────
const FabricTagsDisplay = ({ tags }) => {
  const [showAll, setShowAll] = useState(false);
  const tagList = Array.isArray(tags) ? tags.filter(Boolean) : [];
  if (tagList.length === 0) return null;

  const MAX_VISIBLE = 3;
  const visibleTags = showAll ? tagList : tagList.slice(0, MAX_VISIBLE);
  const remaining = tagList.length - MAX_VISIBLE;

  return (
    <div className="ev-fabric-tags mb-4">
      {visibleTags.map(tag => <span key={tag} className="gh-tag-pill">{tag}</span>)}
      {!showAll && remaining > 0 && (
        <button type="button" className="gh-tag-pill"
          onClick={e => { e.stopPropagation(); setShowAll(true); }}>
          +{remaining} more
        </button>
      )}
      {showAll && tagList.length > MAX_VISIBLE && (
        <button type="button" className="gh-tag-pill"
          onClick={e => { e.stopPropagation(); setShowAll(false); }}>
          See Less
        </button>
      )}
    </div>
  );
};

const DataFabricList = ({
  fabrics, 
  total, 
  page, 
  setPage, 
  size, 
  setSize,
  search, 
  setSearch, 
  filterType, 
  fabricCategories, 
  setFilterType,
  categoryFilter = [], 
  setCategoryFilter,
  onRefresh, 
  onEditFabric, 
  onViewFabricDetails,
  onCardClick,
}) => {
  // Delete fabric states - updated for reusable modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fabricToDelete, setFabricToDelete] = useState(null);
  
  // Search input state (separate from the actual search term)
  const [searchInput, setSearchInput] = useState(search);
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  // API base URL
  const API_BASE_URL = '/pceapi/fabrics';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Show delete confirmation dialog
  const confirmDelete = (fabric) => {
    setFabricToDelete(fabric);
    setShowDeleteConfirm(true);
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    onRefresh();
  };

  // Handle fabric deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!fabricToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting project "${fabricToDelete.name}"`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete`,
        { 
          headers,
          data: {
            fabric_id: fabricToDelete.id
          }
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete project');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(data.message || 'project deleted successfully');
      setIsSuccessPopupVisible(true);
      
    } catch (error) {
      console.error('Error deleting project:', error);
      setPopupErrorMessage(error.message || 'Failed to delete project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setFabricToDelete(null);
    }
  };

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleSearchKeyPress = (e) => { 
    if (e.key === 'Enter') {
      handleSearch(); 
    }
    };

  const filteredFabrics = fabrics;

  // Pagination uses backend total (already filtered by tags on server)
  const totalPages = Math.ceil(total / size);

  // Single card renderer — reused by flat render
  const renderFabricCard = (fabric) => (
    <div key={fabric.id} className="col-4 mb-4">
      <div
        className="ev-card-6 ev-radius-small ev-padding-large h-100 position-relative"
        role="button"
        onClick={() => onCardClick && onCardClick(fabric.id)}
      >
        <div className='pb-3'>
          <div className="row p-0">
            <div className="col-3">
              <h1 className="ev-large-number-dark-blue"><i className="bi bi-boxes"></i></h1>
            </div>
            <div className="col-9 text-end">
              <p className="ev-message-card3">{fabric.thread_count} Data Collections</p>
            </div>
          </div>
          <p className="ev-reg-p-blue mb-2 avatar-tooltip ev-bold-med" data-tooltip={fabric.name || 'None'}>
            <TrimContent text={fabric.name} maxLength={30} />
          </p>
          <div className="mb-2">
            <span className={`badge badge-sm ${fabric.fabric_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
              {fabric.fabric_access_level}
            </span>
            {fabric.team_name && (
              <span className="ev-message-card1 avatar-tooltip ms-2 ev-btn-small ev-bold-med" data-tooltip={fabric.team_name}>
                <i className="bi bi-people"></i>&nbsp;
                <TrimContent text={fabric.team_name} maxLength={20} />
              </span>
            )}
          </div>
          <p className="ev-message-card4 m-0 mb-4 w-100 avatar-tooltip" data-tooltip={fabric.created_by_email}>
            <small><b><i className="bi bi-person-fill"></i> Owner.</b> <TrimContent text={fabric.created_by_email} maxLength={25} /></small>
          </p>
          <p className="ev-small-p-blue mb-0 ev-bold-med">Description</p>
          <p className="ev-xsmall-p-dark mb-2">
            <TrimContent text={fabric.description || 'No description provided'} maxLength={80} />
          </p>
          <p className="ev-small-p-blue mb-0 ev-bold-med">Tags</p>
          <FabricTagsDisplay tags={fabric.category} />
        </div>
        <div className="row position-absolute w-100 bottom-5 right-5">
          <div className="col-7">
            <button className="ev-btn-blue-fill-round ev-bold-reg margin-left-5"
              onClick={(e) => { e.stopPropagation(); onViewFabricDetails(fabric.id); }}>
              Project Details <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div className="col-5 text-end">
            <button className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
              onClick={(e) => { e.stopPropagation(); confirmDelete(fabric); }} data-tooltip="Delete Project">
              <i className="bi bi-trash"></i>
            </button>
            <button className="ev-btn-skyblue-fill-round ev-radius-xsmall pe-2 ps-2 avatar-tooltip ms-2"
              onClick={(e) => { e.stopPropagation(); onEditFabric(fabric); }} data-tooltip="Edit Project">
              <i className="bi bi-pencil"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ev-card-4 w-100 px-5 py-4">
      {/* Toolbar */}
      <div className="d-flex justify-content-between mb-3 mt-1 row">
        <div className="position-relative w-40 col-5">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search by Project name or description"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="col-7 d-flex justify-content-end align-items-center gap-2">
          <CategoryMultiSelect
            categories={fabricCategories}
            selectedCategories={categoryFilter}
            onChange={(val) => { if (setCategoryFilter) setCategoryFilter(val); setPage(1); }}
          />
          <div className="btn-group custom-btn-group-outer">
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'ALL' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('ALL')}
            >
              All
              </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PUBLIC' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PUBLIC')}
            >
              Public
            </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PRIVATE' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PRIVATE')}
            >
              Private
            </button>
          </div>
          <button 
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
            onClick={onRefresh}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh
          </button>
        </div>
      </div>

      {/* Fabric Cards — flat list, no per-tag grouping, backend handles tag filtering */}
      {categoryFilter.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {categoryFilter.map(tag => (
           <span key={tag} className="avatar-tooltip ev-bold-reg py-1 px-2 ev-radius-large ev-fs-11"
          data-tooltip={tag}
          style={{
            background: `${getPillColor(tag)}33`,
            color: getPillColor(tag),
            border: `1.5px solid ${getPillColor(tag)}`,
          }}>
          <i className="bi bi-tag me-1"></i><TrimContent text={tag} maxLength={20} />
        </span>
          ))}
        </div>
      )}

      {filteredFabrics.length === 0 ? (
        <div className="col-12 text-center my-5">
          <p className="ev-reg-p-blue">No Projects found</p>
          {categoryFilter.length > 0 && (
            <p className="col-12 text-center ev-reg-p-blue">
              <i className="bi bi-info-circle"></i> Try removing tag filters to see more projects
            </p>
          )}
        </div>
      ) : (
        <div className="row">{filteredFabrics.map(renderFabricCard)}</div>
      )}

      {/* Pagination */}
      {fabrics.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="ev-small-p-dark">
            Showing {filteredFabrics.length} of {total} entries
          </div>
          <div className="d-flex gap-2">
            <button 
              className="ev-btn-light-fill" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <button className="ev-btn-light-fill" disabled>
              Page {page} of {totalPages || 1}
            </button>
            <button 
              className="ev-btn-light-fill" 
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div>
           <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1); // Reset to first page
              }}
            />
          </div>
        </div>
      )}

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}

      {/* Reusable Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setFabricToDelete(null);
        }}
        onConfirm={handleDelete}
        objectName="project"
        itemName={fabricToDelete ? fabricToDelete.name : ''}
        bodyContent={
          <div>
            <p className="mb-0">This will permanently delete:</p>
            <ul>
              <li>The project metadata and settings</li>
              <li>All Data Collection links within this project</li>
              <li>All feature suites and operational components within this project</li>
            </ul>
            <p className="mb-q">Note: The underlying Data Collections and their documents will not be deleted.</p>
            <p className="text-danger mb-0">This action cannot be undone.</p>
          </div>
        }
      />
    </div>
  );
};

export default DataFabricList;